import { includes, map, partialRight } from 'ramda';

import { Decision } from '@/shared/api/protocol-ts/model/dto_report_common_pb';
import { ConditionCode } from '@/shared/api/protocol-ts/model/dto_report_condition_codes_pb';
import { Condition } from '@/shared/api/protocol-ts/model/dto_report_condition_pb';
import { ToothStatus } from '@/shared/config';
import {
  conditionIsUncertain,
  shouldConditionItemBeShown,
} from '@/shared/embeddedLibs/conditionsAndMasks';

import { getFDAVersion2ConditionCodes, YELLOW_CODES } from '../config';

import { getConditionStatus } from './getConditionStatus';
import { isUserPositiveCertainty } from './isUserPositiveCertainty';

export const isToothMissing = (conditions: Condition[]) =>
  conditions.find(
    (condition) =>
      condition.Code === ConditionCode.Missing &&
      condition.Certainty?.EngineDecision === Decision.PositiveDecision,
  );

type ToothStatusConfig = {
  showLowProbability?: boolean;
  isFDA?: boolean;
  isFDANonAided?: boolean;
  ignoreLowProbability?: boolean;
  isFDA3DGPRestrictions?: boolean;
  isFDAVersion2?: boolean;
  is2DReport?: boolean;
};

export const getToothStatus = (
  toothConditions: Condition[],
  config?: ToothStatusConfig,
) => {
  const {
    showLowProbability = false,
    isFDA = false,
    isFDANonAided = false,
    ignoreLowProbability = false,
    isFDA3DGPRestrictions = false,
    isFDAVersion2 = false,
    is2DReport = false,
  } = config || {};

  const isFDAVersion2Unaided = isFDAVersion2 && isFDANonAided;

  const filteredConditions = toothConditions
    .filter((condition) =>
      shouldConditionItemBeShown(showLowProbability)(condition),
    )
    .filter((condition) => {
      if (condition.Code === ConditionCode.PeriodontalBoneLoss) {
        return false;
      }

      if (
        ignoreLowProbability &&
        YELLOW_CODES.includes(condition.Code) &&
        conditionIsUncertain(condition.Certainty)
      ) {
        return false;
      }

      // Skip FDA condition filtering for FDA 3D GP restrictions mode
      // All conditions should be considered, only status remapping is applied
      if (!isFDA || isFDA3DGPRestrictions) {
        return true;
      }

      // FDA 2.0: the dedicated per-modality conditions list (WA-6322, section 3)
      if (isFDAVersion2) {
        return getFDAVersion2ConditionCodes(is2DReport).includes(
          condition.Code,
        );
      }

      const allowedCodes = [ConditionCode.Missing, ConditionCode.CodeTooth];

      if (!isFDANonAided) {
        allowedCodes.push(ConditionCode.PeriapicalRadiolucency);
      }

      return allowedCodes.includes(condition.Code);
    })
    .filter((condition) => {
      if (isFDAVersion2Unaided) {
        return isUserPositiveCertainty(condition.Certainty);
      }

      return true;
    });

  const allToothStatusByConditions = map(
    partialRight(getConditionStatus, [isToothMissing]),
    filteredConditions,
  );

  const isMissing = isFDAVersion2Unaided
    ? filteredConditions.some(
        (condition) => condition.Code === ConditionCode.Missing,
      )
    : Boolean(isToothMissing(filteredConditions));

  if (isMissing) {
    return ToothStatus.missing;
  }

  if (includes(ToothStatus.lowProbability, allToothStatusByConditions)) {
    // FDA 3D GP restrictions and FDA 2D (unaided) reports: remap lowProbability
    // to healthy (No Findings) — suspicious detections are not shown
    if (isFDA3DGPRestrictions || (isFDA && is2DReport)) {
      return ToothStatus.healthy;
    }
    return ToothStatus.lowProbability;
  }

  if (includes(ToothStatus.unhealthy, allToothStatusByConditions)) {
    return ToothStatus.unhealthy;
  }

  if (includes(ToothStatus.treated, allToothStatusByConditions)) {
    // FDA 3D GP restrictions: remap treated to healthy (No Findings).
    // FDA 2.0 aided keeps the treated (purple) status on the chart.
    if (isFDA3DGPRestrictions && !isFDAVersion2) {
      return ToothStatus.healthy;
    }
    return ToothStatus.treated;
  }

  return ToothStatus.healthy;
};
